import { Box, Typography } from '@mui/material';
import { useChartColors } from '../dashboard/charts/theme';
import { NO_MOTION, SR_ONLY } from './heroStyles';

export interface SpoolGaugeProps {
    /** 0–100. `null` means there is nothing to measure yet; the gauge says so instead of showing 0%. */
    value: number | null;
    label: string;
    /** Short line under the number, e.g. "9,600 of 24,000 pcs". */
    caption?: string;
    /** Text shown in place of the number when `value` is null. */
    emptyText?: string;
    width?: number;
}

// Half ring from 180° to 0° around (60,60). pathLength=100 lets the dash array read as a percentage.
const ARC = 'M 14 60 A 46 46 0 0 1 106 60';
const STRANDS = ['M 17 60 A 43 43 0 0 1 103 60', 'M 11 60 A 49 49 0 0 1 109 60'];

/**
 * A half-ring gauge drawn as thread wound on a spool: a wide track, the wound part in the thread
 * colour with fine strand lines over it, and a flange at each end. The number sits in HTML under the
 * ring (no text in the SVG) so Bangla and long labels wrap freely. Hand SVG, no chart library.
 */
export default function SpoolGauge({ value, label, caption, emptyText = 'Not started', width = 180 }: SpoolGaugeProps) {
    const c = useChartColors();
    const pct = value == null || !Number.isFinite(value) ? null : Math.max(0, Math.min(100, value));
    const shown = pct == null ? emptyText : `${Math.round(pct)}%`;
    const spoken = pct == null ? `${label}: ${emptyText}.` : `${label}: ${Math.round(pct)} percent${caption ? `, ${caption}` : ''}.`;
    return (
        <Box data-testid="spool-gauge" sx={{ width, maxWidth: '100%', textAlign: 'center' }}>
            <Box component="svg" viewBox="0 0 120 68" aria-hidden sx={{ width: '100%', height: 'auto', display: 'block' }}>
                <path d={ARC} fill="none" stroke={c.grid} strokeWidth={14} strokeLinecap="butt" />
                {pct != null && pct > 0 && (
                    <>
                        <Box
                            component="path"
                            data-testid="spool-gauge-wound"
                            d={ARC} pathLength={100} fill="none" stroke={c.series[1]} strokeWidth={14}
                            strokeDasharray={`${pct} 100`}
                            sx={{ transition: 'stroke-dasharray 400ms ease-out', ...NO_MOTION }}
                        />
                        {/* fine strand lines so the wound part reads as layers of thread, not a plain bar */}
                        {STRANDS.map(d => (
                            <path key={d} d={d} pathLength={100} fill="none" stroke={c.surface} strokeOpacity={0.45}
                                  strokeWidth={1} strokeDasharray={`${pct} 100`} />
                        ))}
                    </>
                )}
                {/* spool flanges */}
                <rect x={4} y={58} width={20} height={5} rx={2} fill={c.muted} />
                <rect x={96} y={58} width={20} height={5} rx={2} fill={c.muted} />
            </Box>
            <Typography
                aria-hidden
                sx={{ fontFamily: 'Archivo, sans-serif', fontWeight: 700, fontSize: pct == null ? 16 : 26, lineHeight: 1.1, mt: -0.5 }}
            >
                {shown}
            </Typography>
            <Typography aria-hidden variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                {label}{caption ? ` · ${caption}` : ''}
            </Typography>
            <Box component="span" sx={SR_ONLY}>{spoken}</Box>
        </Box>
    );
}
